/**
 * Estado compartilhado entre as abas da tela de Promoções. Só estado e ações — as regras de
 * cálculo ficam em logic.ts, as chamadas HTTP em promocoesService.ts.
 */
import { createContext, useContext } from 'react'
import type { Anuncio, BaseImposto, Conta, FilaDecisoes, Promocao, StatusLocal, StatusPromo } from './logic'
import type { PromoStatus, ResultadoEscrita } from './promocoesService'

export interface PromoCtxValue {
  contaId: string
  contaCurto: string
  /** Conta com o status das promoções já corrigido pelo que aconteceu nesta sessão. */
  conta: Conta
  base: BaseImposto
  /** Margem alvo da barra do topo: manda na cor de toda M.C. */
  alvo: number
  cmvManual: Map<string, number>
  setCmv: (mlb: string, v: number | null) => void
  live: PromoStatus | null
  /** true = serviço no ar com token válido: os botões escrevem de verdade (ou em dry-run, se o serviço disser). */
  escreve: boolean
  statusLocal: Map<string, StatusLocal>
  marcar: (k: string, status: StatusPromo, preview: boolean) => void
  desmarcar: (k: string) => void
  /**
   * Marca/desmarca várias de uma vez, numa única atualização de estado. Obrigatório em massa:
   * um `marcar` por item em sequência estourava o limite de atualizações do React e deixava a
   * tela branca (23/09/2026, preview com centenas de cupons).
   */
  marcarLote: (itens: Array<{ k: string; status: StatusPromo | null; preview: boolean }>) => void
  sel: Set<string>
  toggleSel: (k: string, on: boolean) => void
  limparSel: () => void
  pickerVal: Map<string, number>
  setPicker: (k: string, v: number) => void
  fila: FilaDecisoes | null
  setFila: (f: FilaDecisoes | null) => void
  abrirAnuncio: (mlb: string) => void
  /** Troca para a aba Robô (painel de exceções, pisos, grupos). */
  abrirRobo: () => void
  abrirModal: (a: Anuncio, p: Promocao) => void
  /**
   * Escrita REAL via serviço (ou simulada em dry-run). Quem chama decide se está em preview.
   * `unidades` só importa na relâmpago (quantas entram na oferta); os outros tipos ignoram.
   */
  ativar: (a: Anuncio, p: Promocao, preco: number, unidades?: number | null) => Promise<ResultadoEscrita>
  remover: (a: Anuncio, p: Promocao) => Promise<ResultadoEscrita>
  /** Frete real por (mlb|preço), preenchido pelo modal quando o serviço está no ar. */
  freteReal: (mlb: string, preco: number) => Promise<number | null>
  /** Recarrega o .json.gz da tela (o serviço republica depois de reler a API e rodar o motor). */
  recarregarBase: () => Promise<void>
}

export const PromoCtx = createContext<PromoCtxValue | null>(null)

export function usePromo(): PromoCtxValue {
  const v = useContext(PromoCtx)
  if (!v) throw new Error('usePromo fora do PromoCtx')
  return v
}
